import React from "react";
import {
  Dog,
  Cat,
  Fish,
  Bird,
  Rabbit,
  Turtle,
  Bone,
  Droplets,
  Thermometer,
  Leaf,
  UtensilsCrossed,
  Bed,
  ToyBrick,
  Stethoscope,
  ShowerHead,
  Home,
  ShieldCheck,
  Waves,
  Bug,
  Trees,
  Flower2,
  PawPrint,
} from "lucide-react";

/* ================= TYPES ================= */
export interface CategoryItem {
  id: number;
  name: string;
  icon: React.ReactNode;
  parentId?: number | null;
  image?: any;
}

/* ──────────────────────────────────────────────
   ICON HELPER — keeps icon size consistent
   ────────────────────────────────────────────── */
const sz = 18; // default icon size (px)

/* ──────────────────────────────────────────────
   1️⃣ TOP-LEVEL PET CATEGORIES (1100000xx)
   ────────────────────────────────────────────── */
export const idPetsCategories: CategoryItem[] = [
  { id: 110000001, name: "Dogs", icon: <Dog size={sz} /> },
  { id: 110000002, name: "Cats", icon: <Cat size={sz} /> },
  { id: 110000003, name: "Fish & Aquatic Pets", icon: <Fish size={sz} /> },
  { id: 110000004, name: "Birds", icon: <Bird size={sz} /> },
  { id: 110000005, name: "Horses", icon: <PawPrint size={sz} /> },
  { id: 110000006, name: "Reptiles & Amphibians", icon: <Turtle size={sz} /> },
  { id: 110000007, name: "Small Animals", icon: <Rabbit size={sz} /> },
];

/* ──────────────────────────────────────────────
   2️⃣ SUBCATEGORIES (1100001xx)
   ────────────────────────────────────────────── */
export const idPetsSubcategories: CategoryItem[] = [
  /* Dogs */
  { id: 110000101, parentId: 110000001, name: "Dog Food", icon: <UtensilsCrossed size={sz} /> },
  { id: 110000102, parentId: 110000001, name: "Beds & Furniture", icon: <Bed size={sz} /> },
  { id: 110000103, parentId: 110000001, name: "Toys & Training", icon: <ToyBrick size={sz} /> },
  { id: 110000104, parentId: 110000001, name: "Health & Wellness", icon: <Stethoscope size={sz} /> },
  { id: 110000105, parentId: 110000001, name: "Grooming", icon: <ShowerHead size={sz} /> },
  { id: 110000106, parentId: 110000001, name: "Leashes & Outdoor", icon: <Trees size={sz} /> },

  /* Cats */
  { id: 110000201, parentId: 110000002, name: "Cat Food", icon: <UtensilsCrossed size={sz} /> },
  { id: 110000202, parentId: 110000002, name: "Litter & Boxes", icon: <Home size={sz} /> },
  { id: 110000203, parentId: 110000002, name: "Cat Furniture", icon: <Bed size={sz} /> },
  { id: 110000204, parentId: 110000002, name: "Toys & Enrichment", icon: <ToyBrick size={sz} /> },
  { id: 110000205, parentId: 110000002, name: "Health & Grooming", icon: <Stethoscope size={sz} /> },

  /* Fish & Aquatic */
  { id: 110000301, parentId: 110000003, name: "Fish Food", icon: <Fish size={sz} /> },
  { id: 110000302, parentId: 110000003, name: "Aquariums & Tanks", icon: <Waves size={sz} /> },
  { id: 110000303, parentId: 110000003, name: "Filters & Pumps", icon: <Droplets size={sz} /> },
  { id: 110000304, parentId: 110000003, name: "Water Care & Test Kits", icon: <Thermometer size={sz} /> },
  { id: 110000305, parentId: 110000003, name: "Decor & Plants", icon: <Flower2 size={sz} /> },

  /* Birds */
  { id: 110000401, parentId: 110000004, name: "Bird Food", icon: <UtensilsCrossed size={sz} /> },
  { id: 110000402, parentId: 110000004, name: "Cages & Stands", icon: <Home size={sz} /> },
  { id: 110000403, parentId: 110000004, name: "Perches & Toys", icon: <ToyBrick size={sz} /> },
  { id: 110000404, parentId: 110000004, name: "Health & Care", icon: <Stethoscope size={sz} /> },

  /* Horses */
  { id: 110000501, parentId: 110000005, name: "Horse Feed", icon: <UtensilsCrossed size={sz} /> },
  { id: 110000502, parentId: 110000005, name: "Tack & Equipment", icon: <ShieldCheck size={sz} /> },
  { id: 110000503, parentId: 110000005, name: "Stable Supplies", icon: <Home size={sz} /> },
  { id: 110000504, parentId: 110000005, name: "Grooming & Care", icon: <ShowerHead size={sz} /> },

  /* Reptiles */
  { id: 110000601, parentId: 110000006, name: "Food & Supplements", icon: <Bug size={sz} /> },
  { id: 110000602, parentId: 110000006, name: "Terrariums", icon: <Home size={sz} /> },
  { id: 110000603, parentId: 110000006, name: "Heating & Lighting", icon: <Thermometer size={sz} /> },
  { id: 110000604, parentId: 110000006, name: "Habitat Decor", icon: <Leaf size={sz} /> },

  /* Small Animals */
  { id: 110000701, parentId: 110000007, name: "Food & Treats", icon: <Bone size={sz} /> },
  { id: 110000702, parentId: 110000007, name: "Cages & Habitats", icon: <Home size={sz} /> },
  { id: 110000703, parentId: 110000007, name: "Bedding & Litter", icon: <Bed size={sz} /> },
  { id: 110000704, parentId: 110000007, name: "Toys & Exercise", icon: <ToyBrick size={sz} /> },
];

/* ──────────────────────────────────────────────
   3️⃣ PRODUCTS (1100101xx+)
   ────────────────────────────────────────────── */
export const idPetsProducts: CategoryItem[] = [
  /* Dog Food */
  { id: 110010101, parentId: 110000101, name: "Dry Dog Food", icon: <Bone size={sz} /> },
  { id: 110010102, parentId: 110000101, name: "Wet Dog Food", icon: <UtensilsCrossed size={sz} /> },

  /* Cat Food */
  { id: 110020101, parentId: 110000201, name: "Dry Cat Food", icon: <Bone size={sz} /> },
  { id: 110020102, parentId: 110000201, name: "Wet Cat Food", icon: <UtensilsCrossed size={sz} /> },

  /* Fish */
  { id: 110030101, parentId: 110000301, name: "Tropical Fish Food", icon: <Fish size={sz} /> },
  { id: 110030102, parentId: 110000301, name: "Bottom Feeder Food", icon: <Fish size={sz} /> },

  /* Habitats */
  { id: 110030201, parentId: 110000302, name: "Glass Aquariums", icon: <Waves size={sz} /> },
  { id: 110060201, parentId: 110000602, name: "Reptile Terrariums", icon: <Home size={sz} /> },

  /* Accessories */
  { id: 110010601, parentId: 110000106, name: "Dog Leashes & Harnesses", icon: <Trees size={sz} /> },
  { id: 110020301, parentId: 110000203, name: "Cat Trees & Scratching Posts", icon: <Bed size={sz} /> },
];